import { Injectable } from "@angular/core";
import { Company } from "../../models/domains/pbm/companies/company";
import { CompanyRepository } from "../../repositories/pbm/company.repository";
import { PageableResult } from "../../models/domains/paginator/pageable-result";
import { PagingRequest } from "../../models/domains/paginator/paging-request";
import { CompanyContact } from "../../models/domains/pbm/companies/company-contact";
import { CompanyMasterData } from "../../models/domains/pbm/companies/company-master-data";
import { ExportOrderKey } from "../../models/domains/pbm/exports/export-order-key";
import { CompanyFinancialReportsPageableResult } from "../../models/domains/pbm/companies/company-financial-reports-pageable-result.model";
import { FiscalNumber } from "../../models/domains/pbm/fiscal-number";
import { ICompanyFilterParams } from "../../models/domains/pbm/companies/company-filter-params";
import { CompanyConfiguration } from "../../models/domains/pbm/companies/company-configuration";
import { CompanyRemainingLimit } from "../../models/domains/pbm/companies/company-remaining-limit";
import { XLSXFileExportResult } from "../../models/domains/pbm/exports/file-export-result";
import { SelectionModel } from "@angular/cdk/collections";
import { MatTableDataSource } from "@angular/material/table";

@Injectable({ providedIn: "root" })
export class CompanyService {

  private processStatus: number = 0;
  private allowedCompanies = new MatTableDataSource<Company>();
  private selectedCompanies = new SelectionModel<Company>(true, []);
  private messageStatus: string;
  private filters: {
    startAt: string;
    endAt: string;
    fiscalNumber: string;
  } = { startAt: "", endAt: "", fiscalNumber: "" };
  private readonly intervalInMillis: number = 5000;
  private intervalHandle: any;

  constructor(private companyRepository: CompanyRepository) {}

  public async searchCompany(filterParams: ICompanyFilterParams, pagingRequest: PagingRequest): Promise<PageableResult> {
    return this.companyRepository.searchCompany(filterParams, pagingRequest);
  }

  public async getFinancialReportCompany(url: string): Promise<XLSXFileExportResult> {
    return this.companyRepository.getFinancialReportCompany(url);
  }

  public async findCompanyBy(fiscalNumber: string): Promise<Company> {
    return this.companyRepository.findCompanyBy(fiscalNumber);
  }

  public async addCompany(company: Company): Promise<any> {
    return this.companyRepository.addCompany(company);
  }

  public async addCompanyContact(companyContact: CompanyContact): Promise<any> {
    return this.companyRepository.addCompanyContact(companyContact);
  }

  public async findContactBy(fiscalNumber: string): Promise<Array<CompanyContact>> {
    return this.companyRepository.findContactBy(fiscalNumber);
  }

  public async editCompanyContact(companyContact: CompanyContact): Promise<any> {
    return this.companyRepository.editCompanyContact(companyContact);
  }

  public async deleteCompanyContact(companyContactKey: string): Promise<any> {
    return this.companyRepository.deleteCompanyContact(companyContactKey);
  }

  public async searchCompanyByFicalNumberFromMDM(fiscalNumber: FiscalNumber): Promise<CompanyMasterData> {
    return this.companyRepository.searchCompanyByFicalNumberFromMDM(fiscalNumber);
  }

  public async findCompanyExportOrderKey(): Promise<ExportOrderKey> {
    return await this.companyRepository.findCompanyExportOrderKey();
  }

  public async getCompanyFinancialReports(pagingRequest: PagingRequest): Promise<CompanyFinancialReportsPageableResult> {
    return await this.companyRepository.getCompanyFinancialReports(pagingRequest);
  }

  public async companyConfigurations(fiscalNumber: string): Promise<CompanyConfiguration> {
    return await this.companyRepository.companyConfigurations(fiscalNumber);
  }

  public async updateCompanyConfigurations(fiscalNumber: string, transactionalLimit: number): Promise<CompanyConfiguration> {
    return await this.companyRepository.updateCompanyConfigurations(fiscalNumber, transactionalLimit);
  }

  public async createCompanyConfigurations(fiscalNumber: string, transactionalLimit: number): Promise<CompanyConfiguration> {
    return await this.companyRepository.createCompanyConfigurations(fiscalNumber, transactionalLimit);
  }

  public async getCompanyRemainingLimit(fiscalNumber: string): Promise<CompanyRemainingLimit> {
    return this.companyRepository.getCompanyRemainingLimit(fiscalNumber);
  }

  clearAllFilters() {
    clearInterval(this.intervalHandle);
    this.allowedCompanies.data = [];
    this.selectedCompanies.clear();
    this.filters = { startAt: "", endAt: "", fiscalNumber: "" };
    this.processStatus = 0;
    this.messageStatus = "";
  }
}
